import { type MergeTagOptions } from '@/lib/mergeTags'
import { type WhatsappTemplate } from '../types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type HeaderImageType = 'attendee.qr_code' | 'custom_url'
export type CustomImageMode = 'url' | 'upload'

export type SelectOptionGroup = {
  label: string
  options: { label: string; value: string }[]
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const MAX_HEADER_IMAGE_SIZE_MB = 5
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp']

/**
 * Mapping for a slot the template hardcodes but this event has no data for — a
 * {{2}} surname on a form that collects one Full Name field. Mirrors
 * `Whatsapp::TemplateParameters::BLANK_TAG`; the sender turns it into a
 * non-breaking space, since Meta rejects a parameter that is actually empty.
 */
export const BLANK_MERGE_TAG = '__blank__'
export const BLANK_MERGE_TAG_LABEL = 'Leave blank'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const titleize = (key: string): string =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

export const labelForMergeTagValue = (
  value: string,
  mergeTagOptions: MergeTagOptions,
): string => {
  if (!value) return value
  if (value === BLANK_MERGE_TAG) return BLANK_MERGE_TAG_LABEL

  const [prefix, ...rest] = value.split('.')
  const key = rest.join('.')

  if (prefix === 'attendee') {
    const regField = mergeTagOptions.registrationFields.find(
      (f) => f.identifier === key,
    )
    if (regField) return regField.label ?? titleize(key)
    return `Attendee ${titleize(key)}`
  }

  if (prefix === 'event') {
    return `Event ${titleize(key)}`
  }

  return value
}

export const detectParameterSlots = (body: string): number[] => {
  const matches = Array.from(body.matchAll(/\{\{(\d+)\}\}/g))
  const keys = new Set(matches.map((m) => parseInt(m[1], 10)))
  return Array.from(keys).sort((a, b) => a - b)
}

export const renderPreview = (
  body: string,
  paramValues: Record<string, string>,
  mergeTagOptions: MergeTagOptions,
): string =>
  body.replace(/\{\{(\d+)\}\}/g, (_match, key) => {
    const value = paramValues[key]
    if (!value) return `{{${key}}}`
    // Shown as the gap it will actually leave in the delivered message.
    if (value === BLANK_MERGE_TAG) return ''
    const label = labelForMergeTagValue(value, mergeTagOptions)
    return `[${label}]`
  })

/**
 * Option groups for every merge-tag picker on the WhatsApp screens. "Leave blank"
 * leads, because it is the answer whenever the template asks for a variable the
 * event's registration form does not collect.
 */
export const buildSelectOptionGroups = (
  mergeTagOptions: MergeTagOptions,
): SelectOptionGroup[] => {
  const attendeeOptions: { label: string; value: string }[] = []

  mergeTagOptions.attendeeKeys.forEach((key) => {
    attendeeOptions.push({ label: titleize(key), value: `attendee.${key}` })
  })

  mergeTagOptions.registrationFields.forEach((field) => {
    const label = field.label ?? titleize(field.identifier)
    const value = `attendee.${field.identifier}`
    if (!attendeeOptions.some((option) => option.value === value)) {
      attendeeOptions.push({ label, value })
    }
  })

  const eventOptions = mergeTagOptions.eventKeys.map((key) => ({
    label: titleize(key),
    value: `event.${key}`,
  }))

  return [
    { label: 'No value', options: [{ label: BLANK_MERGE_TAG_LABEL, value: BLANK_MERGE_TAG }] },
    { label: 'Attendee', options: attendeeOptions },
    { label: 'Event', options: eventOptions },
  ]
}

/** Slots present in the body but left unmapped — Meta rejects the whole send. */
export const unmappedSlots = (
  slots: number[],
  paramValues: Record<string, string>,
): number[] => slots.filter((slot) => !paramValues[String(slot)])

/** Returns true when the header text contains a {{1}} placeholder. Accepts a template object or raw string. */
export const headerTextHasPlaceholder = (input: WhatsappTemplate | string): boolean => {
  const text = typeof input === 'string' ? input : input.headerText
  return !!(text && /\{\{1\}\}/.test(text))
}

export const renderHeaderTextPreview = (
  headerText: string,
  headerTextValue: string | undefined,
  mergeTagOptions: MergeTagOptions,
): string => {
  if (!/\{\{1\}\}/.test(headerText)) return headerText
  if (!headerTextValue) return headerText
  const label = labelForMergeTagValue(headerTextValue, mergeTagOptions)
  return headerText.replace('{{1}}', `[${label}]`)
}
